/* eslint-disable @typescript-eslint/no-unsafe-member-access */
/* eslint-disable @typescript-eslint/no-unsafe-assignment */
import { AxiosError } from 'axios';
import _ from 'lodash';

import { APIService } from '@/src/infrastructure/Protocol/APIService';

import { ICampaignsService, IEmblueApiMethodNameMap } from './ICampaignsService';

import {
  ICampaignActions,
  ICampaignActionsListPayload,
  ICampaignsActionsResponse,
  IEditActionPayload,
  IEditActionResponse,
  ITableCampaignActions,
} from '@/modules/CampaignsModule/interfaces/CampaignActions';
import {
  ICampaign,
  ICampaignDetail,
  ICampaignDetailResponse,
  ICampaignDetailResult,
  ICampaignListPayload,
  ICampaignsListResponse,
  ICampaignsStrategiesResponse,
  ICreateEditCampaigns,
  ICreateEditCampaignsResponse,
  IStrategy,
  ITableCampaigns,
  TCampaignID,
} from '@/modules/CampaignsModule/interfaces/Campaigns';

const STATUS_SUCCESS = 200;

/* eslint-disable @typescript-eslint/no-explicit-any */
export class CampaignsService implements ICampaignsService {
  private api: APIService;
  private pathCampaigns = 'api/v2.1/campaigns';

  private constructor(api: APIService) {
    this.api = api;
  }

  /**
   * @param error AxiosError<Error>
   * @description Catch errors from API
   * @returns void
   */
  private catchErrors(error: AxiosError<Error>, method: string): void {
    const { message } = error;
    console.error(`❌ EM_Error CampaignsService[${method}]:`, message);
  }

  /**
   * @param api APIService
   * @returns {ICampaignsService}
   * @description Create new instance of CampaignsService
   */
  static create(api: APIService): ICampaignsService {
    return new CampaignsService(api);
  }

  /**
   * @param query: { campaign: ICreateEditCampaigns }
   * @returns {Promise<ICreateEditCampaignsResponse>}
   * @description Create new campaign
   */
  async createCampaign(query: {
    campaign: ICreateEditCampaigns;
  }): Promise<ICreateEditCampaignsResponse> {
    try {
      const { campaign } = query;
      const { status } = await this.api.post(`${this.pathCampaigns}/create`, campaign);
      return { statusOK: status === STATUS_SUCCESS };
    } catch (error) {
      this.catchErrors(error as AxiosError<Error>, 'createCampaign');
      return { statusOK: false };
    }
  }

  /**
   * @param query: { campaign: ICreateEditCampaigns }
   * @returns {Promise<ICreateEditCampaignsResponse>}
   * @description Create new campaign
   */
  async getStrategiesByAccount(): Promise<ICampaignsStrategiesResponse> {
    try {
      const { status, data } = await this.api.get(`${this.pathCampaigns}/strategies/list`);
      const { result } = data as { result: IStrategy[] };
      return { statusOK: status === STATUS_SUCCESS, strategies: result };
    } catch (error) {
      this.catchErrors(error as AxiosError<Error>, 'getStrategiesByAccount');
      return { statusOK: false, strategies: [] };
    }
  }

  /**
   * @description The getCampaign function retrieves a list of campaigns from an API and returns them as a Promise.
   * @param {}
   * @returns {Promise<ICampaignsListResponse>}
   */
  async getCampaign(query: ICampaignListPayload): Promise<ICampaignsListResponse> {
    const { page, limit, search, state, strategy, startDate, endDate, sortField, sortOrder } =
      query;
    try {
      const { data } = await this.api.post(`${this.pathCampaigns}/list`, {
        page,
        limit,
        search,
        state,
        strategy,
        startDate,
        endDate,
        sortField,
        sortOrder,
      });
      const { result } = data as { result: ICampaign[] };
      const { pager } = data as { pager: { countTotal: number; countPartial: number } };

      const listCampaignsFlatten = _.flatten(result).map((x) => ({ campaign: { ...x } }));
      const listCampaigns: ITableCampaigns[] = listCampaignsFlatten;

      return { campaigns: listCampaigns, total: pager.countTotal, partial: pager.countPartial };
    } catch (error) {
      this.catchErrors(error as AxiosError<Error>, 'getCampaign');
      return { statusOK: false, campaigns: [] };
    }
  }

  /**
   * @param query: { campaignID: TCampaignID }
   * @returns {Promise<ICreateEditCampaignsResponse>}
   * @description Create new campaign
   */
  async getCampaignByID(campaignID: TCampaignID): Promise<ICampaignDetailResponse> {
    try {
      const { data } = await this.api.get(`${this.pathCampaigns}/detail?campaignId=${campaignID}`);
      const dataResult: ICampaignDetailResult = data as ICampaignDetailResult;
      const campaign: ICampaignDetail = dataResult.result[0] ?? {};
      return { campaign };
    } catch (error) {
      this.catchErrors(error as AxiosError<Error>, 'getCampaignByID');
      return { statusOK: false, campaign: {} };
    }
  }

  //TODO: Next sprint implement search, filters & pagination
  /**
   * @param query: {ICampaignActionsListPayload}
   * @returns {Promise<ICampaignsActionsResponse>}
   * @description Get list actions by campaign
   */
  async getActionsByCampaign(
    query: ICampaignActionsListPayload
  ): Promise<ICampaignsActionsResponse> {
    try {
      const { data } = await this.api.post(`${this.pathCampaigns}/actions/list`, query);
      const { result } = data as { result: ICampaignActions[] };
      const { pager } = data as { pager: { countTotal: number; countPartial: number } };

      const listCampaignActionsFlatten = _.flatten(result).map((x) => ({ action: { ...x } }));
      const listCampaigns: ITableCampaignActions[] = listCampaignActionsFlatten;

      return { actions: listCampaigns, total: pager.countTotal, partial: pager.countPartial };
    } catch (error) {
      this.catchErrors(error as AxiosError<Error>, 'getActionsByCampaign');
      return { statusOK: false, actions: [] };
    }
  }

  /**
   * @param query: {IEditActionPayload}
   * @returns {Promise<IEditActionResponse>}
   * @description Update name action
   */
  async editAction(query: IEditActionPayload): Promise<IEditActionResponse> {
    try {
      const { status } = await this.api.patch(`${this.pathCampaigns}/actions/edit`, query);
      return { statusOK: status === STATUS_SUCCESS };
    } catch (error) {
      this.catchErrors(error as AxiosError<Error>, 'getActionsByCampaign');
      return { statusOK: false };
    }
  }
}

export const CampaignsServiceMethods: IEmblueApiMethodNameMap = Object.getOwnPropertyNames(
  CampaignsService.prototype
).reduce((h: any, k: any) => {
  h[k] = k;
  return h;
}, {} as typeof CampaignsService);
